import type { GamePlayerStatColumnEnum, GPEventResponse, GPGamePlayerResponse } from "../../types";
import { PlayerStatCalculationResult, PlayerStatType } from "./definition";
import { formatPrecomputedGoalkeeperStat, type GamePlayerStatLookup } from "./precomputedGoalkeeperStats";

const ADMINISTRATIVE_CATEGORY = "administrative";

const EVENT_TYPE = {
	pass: "pass",
	shot: "shot",
	save: "save",
	catch: "catch",
	punch: "punch",
	catchInterception: "catch_interception",
	clearance: "clearance",
	block: "block",
	tackle: "tackle",
	interception: "interception",
	aerialDuel: "aerial_duel",
	recovery: "recovery",
} as const;

const EVENT_DETAIL = {
	bodyPart: "body_part",
	passDistanceM: "pass_distance_m",
	outsideGkPa: "outside_gk_pa",
	crossEntersPa: "cross_enters_pa",
	goalDistanceM: "goal_distance_m",
	cross: "cross",
	length: "length",
	onTarget: "on_target",
	saveType: "save_type",
} as const;

const EVENT_DETAIL_VALUE = {
	bodyPartHand: "hand",
	lengthLong: "long",
	onTargetOn: "on",
	saveTypeCatch: "catch",
	saveTypeBlock: "block",
	saveTypeParry: "parry",
} as const;

const GK_DEFENSIVE_EVENT_TYPES = new Set<string>([
	EVENT_TYPE.save,
	EVENT_TYPE.catch,
	EVENT_TYPE.punch,
	EVENT_TYPE.catchInterception,
	EVENT_TYPE.clearance,
	EVENT_TYPE.block,
	EVENT_TYPE.tackle,
	EVENT_TYPE.interception,
	EVENT_TYPE.aerialDuel,
	EVENT_TYPE.recovery,
]);

interface PeriodFilteredGoalkeeperMetrics {
	throwsSuccess: number;
	throwsTotal: number;
	passLengthTotalM: number;
	passLengthCount: number;
	longPassesTotal: number;
	passesTotal: number;
	defensiveActionsOpaTotal: number;
	defActionDistanceTotalM: number;
	defActionDistanceCount: number;
	crossesFacedTotal: number;
	crossesStoppedTotal: number;
	saveCatchTotal: number;
	saveBlockOrParryTotal: number;
	shotsOnTargetFacedTotal: number;
}

function getEventDetails(event: GPEventResponse): Record<string, unknown> | null {
	if (event.details == null || typeof event.details !== "object" || Array.isArray(event.details)) {
		return null;
	}

	return event.details as Record<string, unknown>;
}

function detailStringEquals(event: GPEventResponse, key: string, expected: string): boolean {
	const details = getEventDetails(event);
	if (details === null) {
		return false;
	}

	const detailValue = details[key];
	if (detailValue === undefined || detailValue === null) {
		return false;
	}

	if (typeof detailValue === "boolean") {
		return (expected === "true" && detailValue === true) || (expected === "false" && detailValue === false);
	}

	return String(detailValue) === expected;
}

function detailBooleanEquals(event: GPEventResponse, key: string, expected: boolean): boolean {
	const details = getEventDetails(event);
	if (details === null) {
		return false;
	}

	const detailValue = details[key];
	if (typeof detailValue === "boolean") {
		return detailValue === expected;
	}

	if (detailValue === "true") {
		return expected === true;
	}

	if (detailValue === "false") {
		return expected === false;
	}

	return false;
}

function detailNumber(event: GPEventResponse, key: string): number | null {
	const details = getEventDetails(event);
	if (details === null) {
		return null;
	}

	const detailValue = details[key];
	if (typeof detailValue === "number" && Number.isFinite(detailValue)) {
		return detailValue;
	}

	if (typeof detailValue === "string" && detailValue !== "") {
		const parsed = Number(detailValue);
		if (Number.isFinite(parsed)) {
			return parsed;
		}
	}

	return null;
}

function isEventInSelectedPeriods(event: GPEventResponse, periods: string[]): boolean {
	const eventPeriod = event.half == null ? "--" : event.half.toString();

	return periods.includes(eventPeriod);
}

function hasRecordedSuccessOutcome(event: GPEventResponse): boolean {
	return event.success !== null && event.success !== undefined;
}

function isSuccessfulEvent(event: GPEventResponse): boolean {
	return event.success === true;
}

function isGkThrow(event: GPEventResponse): boolean {
	if (event.type !== EVENT_TYPE.pass || event.category === ADMINISTRATIVE_CATEGORY) {
		return false;
	}

	return detailStringEquals(event, EVENT_DETAIL.bodyPart, EVENT_DETAIL_VALUE.bodyPartHand);
}

function isGkPass(event: GPEventResponse): boolean {
	return event.type === EVENT_TYPE.pass && event.category !== ADMINISTRATIVE_CATEGORY;
}

function isGkDefensiveAction(event: GPEventResponse): boolean {
	if (event.category === ADMINISTRATIVE_CATEGORY || event.type == null) {
		return false;
	}

	return GK_DEFENSIVE_EVENT_TYPES.has(event.type);
}

function isSaveCatch(event: GPEventResponse): boolean {
	return event.type === EVENT_TYPE.save && detailStringEquals(event, EVENT_DETAIL.saveType, EVENT_DETAIL_VALUE.saveTypeCatch);
}

function isSaveBlockOrParry(event: GPEventResponse): boolean {
	if (event.type !== EVENT_TYPE.save) {
		return false;
	}

	return (
		detailStringEquals(event, EVENT_DETAIL.saveType, EVENT_DETAIL_VALUE.saveTypeBlock)
		|| detailStringEquals(event, EVENT_DETAIL.saveType, EVENT_DETAIL_VALUE.saveTypeParry)
	);
}

function isGkCrossIntervention(event: GPEventResponse): boolean {
	return (
		event.type === EVENT_TYPE.catch
		|| event.type === EVENT_TYPE.punch
		|| isSaveCatch(event)
		|| event.type === EVENT_TYPE.catchInterception
		|| event.type === EVENT_TYPE.recovery
	);
}

function isCrossEnteringPenaltyArea(event: GPEventResponse): boolean {
	return detailStringEquals(event, EVENT_DETAIL.cross, "true") && detailBooleanEquals(event, EVENT_DETAIL.crossEntersPa, true);
}

function isShotOnTarget(event: GPEventResponse): boolean {
	return event.type === EVENT_TYPE.shot && detailStringEquals(event, EVENT_DETAIL.onTarget, EVENT_DETAIL_VALUE.onTargetOn);
}

function eventSortTime(event: GPEventResponse): number {
	if (Number.isFinite(event.videoTime)) {
		return event.videoTime;
	}

	return event.footballTime ?? 0;
}

function sortEventsByTimeline(events: GPEventResponse[]): GPEventResponse[] {
	return [...events].sort((left, right) => {
		const leftHalf = left.half ?? 0;
		const rightHalf = right.half ?? 0;
		if (leftHalf !== rightHalf) {
			return leftHalf - rightHalf;
		}

		const timeDelta = eventSortTime(left) - eventSortTime(right);
		if (timeDelta !== 0) {
			return timeDelta;
		}

		return left.id - right.id;
	});
}

function findNextOperationalNeighbour(sortedEvents: GPEventResponse[], fromEvent: GPEventResponse): GPEventResponse | null {
	const fromIndex = sortedEvents.indexOf(fromEvent);
	if (fromIndex < 0) {
		return null;
	}

	for (let nextIndex = fromIndex + 1; nextIndex < sortedEvents.length; nextIndex += 1) {
		const candidate = sortedEvents[nextIndex];
		if (candidate.half !== fromEvent.half) {
			return null;
		}

		if (candidate.category === ADMINISTRATIVE_CATEGORY) {
			return null;
		}

		if (candidate.gamePlayerId == null || candidate.type == null) {
			continue;
		}

		return candidate;
	}

	return null;
}

function ratioPercent(success: number, total: number): string | null {
	if (total === 0) {
		return null;
	}

	return String(Math.round((success / total) * 100));
}

function averageFromTotalAndCount(total: number, count: number): string | null {
	if (count === 0) {
		return null;
	}

	return (total / count).toFixed(1);
}

function emptyMetrics(): PeriodFilteredGoalkeeperMetrics {
	return {
		throwsSuccess: 0,
		throwsTotal: 0,
		passLengthTotalM: 0,
		passLengthCount: 0,
		longPassesTotal: 0,
		passesTotal: 0,
		defensiveActionsOpaTotal: 0,
		defActionDistanceTotalM: 0,
		defActionDistanceCount: 0,
		crossesFacedTotal: 0,
		crossesStoppedTotal: 0,
		saveCatchTotal: 0,
		saveBlockOrParryTotal: 0,
		shotsOnTargetFacedTotal: 0,
	};
}

function accumulateOwnedGoalkeeperMetrics(event: GPEventResponse, metrics: PeriodFilteredGoalkeeperMetrics): void {
	if (isSaveCatch(event)) {
		metrics.saveCatchTotal += 1;
	}

	if (isSaveBlockOrParry(event)) {
		metrics.saveBlockOrParryTotal += 1;
	}

	if (isGkThrow(event) && hasRecordedSuccessOutcome(event)) {
		metrics.throwsTotal += 1;
		if (isSuccessfulEvent(event)) {
			metrics.throwsSuccess += 1;
		}
	}

	if (isGkPass(event)) {
		const passDistanceM = detailNumber(event, EVENT_DETAIL.passDistanceM);
		if (passDistanceM !== null) {
			metrics.passLengthTotalM += passDistanceM;
			metrics.passLengthCount += 1;
		}
	}

	if (event.type === EVENT_TYPE.pass && hasRecordedSuccessOutcome(event)) {
		metrics.passesTotal += 1;
		if (detailStringEquals(event, EVENT_DETAIL.length, EVENT_DETAIL_VALUE.lengthLong)) {
			metrics.longPassesTotal += 1;
		}
	}

	if (isGkDefensiveAction(event)) {
		if (detailBooleanEquals(event, EVENT_DETAIL.outsideGkPa, true)) {
			metrics.defensiveActionsOpaTotal += 1;
		}

		const goalDistanceM = detailNumber(event, EVENT_DETAIL.goalDistanceM);
		if (goalDistanceM !== null) {
			metrics.defActionDistanceTotalM += goalDistanceM;
			metrics.defActionDistanceCount += 1;
		}
	}
}

function isCrossStoppedByGoalkeeper(
	crossEvent: GPEventResponse,
	sortedTimeline: GPEventResponse[],
	gamePlayerId: number,
	gameTeamId: number,
): boolean {
	const nextEvent = findNextOperationalNeighbour(sortedTimeline, crossEvent);
	if (nextEvent === null) {
		return false;
	}

	return (
		nextEvent.gameTeamId === gameTeamId
		&& nextEvent.gamePlayerId === gamePlayerId
		&& isGkCrossIntervention(nextEvent)
	);
}

function aggregatePeriodFilteredGoalkeeperMetrics(
	events: GPEventResponse[],
	gamePlayerId: number,
	gameTeamId: number,
	periods: string[],
): PeriodFilteredGoalkeeperMetrics {
	const metrics = emptyMetrics();
	const periodEvents = events.filter((event) => isEventInSelectedPeriods(event, periods));
	const sortedTimeline = sortEventsByTimeline(periodEvents);

	periodEvents.forEach((event) => {
		if (event.gamePlayerId === gamePlayerId) {
			accumulateOwnedGoalkeeperMetrics(event, metrics);
		}

		if (event.gameTeamId === gameTeamId) {
			return;
		}

		if (isCrossEnteringPenaltyArea(event)) {
			metrics.crossesFacedTotal += 1;
			if (isCrossStoppedByGoalkeeper(event, sortedTimeline, gamePlayerId, gameTeamId)) {
				metrics.crossesStoppedTotal += 1;
			}
		}

		if (isShotOnTarget(event)) {
			metrics.shotsOnTargetFacedTotal += 1;
		}
	});

	return metrics;
}

function toNullableCount(value: number): string | null {
	return String(value);
}

function buildPeriodFilteredLookup(
	gamePlayerId: number,
	metrics: PeriodFilteredGoalkeeperMetrics,
): GamePlayerStatLookup {
	const columnMap = new Map<GamePlayerStatColumnEnum, string | null>();
	const throwsRatio = ratioPercent(metrics.throwsSuccess, metrics.throwsTotal);
	const launchPercent = ratioPercent(metrics.longPassesTotal, metrics.passesTotal);
	const crossesStoppedRatio = ratioPercent(metrics.crossesStoppedTotal, metrics.crossesFacedTotal);
	const savePercent = ratioPercent(
		metrics.saveCatchTotal + metrics.saveBlockOrParryTotal,
		metrics.shotsOnTargetFacedTotal,
	);

	columnMap.set("throwsSuccess", toNullableCount(metrics.throwsSuccess));
	columnMap.set("throwsTotal", toNullableCount(metrics.throwsTotal));
	columnMap.set("throwsRatio", throwsRatio);
	columnMap.set("averagePassLength", averageFromTotalAndCount(metrics.passLengthTotalM, metrics.passLengthCount));
	columnMap.set("launchPercent", launchPercent);
	columnMap.set("defensiveActionsOpaTotal", toNullableCount(metrics.defensiveActionsOpaTotal));
	columnMap.set("averageDefensiveActionDistance", averageFromTotalAndCount(metrics.defActionDistanceTotalM, metrics.defActionDistanceCount));
	columnMap.set("crossesFacedTotal", toNullableCount(metrics.crossesFacedTotal));
	columnMap.set("crossesStoppedTotal", toNullableCount(metrics.crossesStoppedTotal));
	columnMap.set("crossesStoppedRatio", crossesStoppedRatio);
	columnMap.set("savePercent", savePercent);

	const lookup: GamePlayerStatLookup = new Map();
	lookup.set(gamePlayerId, columnMap);

	return lookup;
}

export function calculatePeriodFilteredGoalkeeperStat(
	statType: PlayerStatType,
	player: GPGamePlayerResponse,
	periods: string[],
	events: GPEventResponse[],
): PlayerStatCalculationResult {
	if (player.id === null || player.teamId == null) {
		return formatPrecomputedGoalkeeperStat(statType, player, new Map());
	}

	const metrics = aggregatePeriodFilteredGoalkeeperMetrics(events, player.id, player.teamId, periods);

	return formatPrecomputedGoalkeeperStat(
		statType,
		player,
		buildPeriodFilteredLookup(player.id, metrics),
	);
}
